import { B2BAdminServicePromiseClient } from "../../../gen/witbooking/backoffice/b2b/v1/b2b_admin_api_grpc_web_pb";
import {
  AdminEntity,
  CommercialTermsRow,
  CommercialTermsRowKey,
  DeleteCommercialTermsRowRequest,
  ListCommercialTermsRequest,
  ListEntitiesRequest,
  UpdateCommercialTermsRowRequest,
} from "../../../gen/witbooking/backoffice/b2b/v1/b2b_admin_api_pb";
import { addAuthzMetadataGrpcCall, buildGrpcClient } from "../../../lib/grpc";

/** The fields this UI reads from one B2B entity. */
export interface B2BEntityDto {
  entityId: string;
  externalId: string;
  entityType: "company" | "agency";
  legalName: string;
  credit: boolean;
}

/** The fields this UI reads from one published agreement line or default commission. */
export interface CommercialTermsRowDto {
  entityId: string;
  entityName: string;
  agreementId?: string;
  lineId?: string;
  counterpartyName?: string;
  commissionPct?: number;
  net: boolean;
  validFrom?: string;
  validTo?: string;
  hotelCodes?: string[];
  kind: "default" | "line";
  appliesTo?: string;
}

/** What master mode can change in one row. */
export interface CommercialTermsRowChangeDto {
  commissionPct?: number;
  net: boolean;
  validFrom?: string;
  validTo?: string;
  hotelCodes: string[];
}

// 200 is the server's page cap; every page is fetched and the screens filter client side.
const PAGE_SIZE = 200;

const client = buildGrpcClient(B2BAdminServicePromiseClient);

// Proto strings are "" when not informed; the screens expect the field absent.
const informed = (value: string) => value || undefined;

function toEntity(entity: AdminEntity): B2BEntityDto {
  return {
    entityId: entity.getEntityId(),
    externalId: entity.getExternalId(),
    entityType: entity.getEntityType() as B2BEntityDto["entityType"],
    legalName: entity.getLegalName(),
    credit: entity.getCredit(),
  };
}

function toRow(row: CommercialTermsRow): CommercialTermsRowDto {
  const pct = row.getCommissionPct();
  return {
    entityId: row.getEntityId(),
    entityName: row.getEntityName(),
    agreementId: informed(row.getAgreementId()),
    lineId: informed(row.getLineId()),
    counterpartyName: informed(row.getCounterpartyName()),
    commissionPct: pct ? Number(pct) : undefined,
    net: row.getNet(),
    validFrom: informed(row.getValidFrom()),
    validTo: informed(row.getValidTo()),
    hotelCodes: row.getHotelCodesList(),
    kind: row.getKind() === "default" ? "default" : "line",
    appliesTo: informed(row.getAppliesTo()),
  };
}

function toKey(row: CommercialTermsRowDto): CommercialTermsRowKey {
  const key = new CommercialTermsRowKey();
  key.setEntityId(row.entityId);
  key.setKind(row.kind);
  key.setAgreementId(row.agreementId ?? "");
  key.setLineId(row.lineId ?? "");
  key.setAppliesTo(row.appliesTo ?? "");
  return key;
}

export async function listB2BEntities(): Promise<B2BEntityDto[]> {
  const call = addAuthzMetadataGrpcCall(client, client.listEntities);
  const response = await call(new ListEntitiesRequest());
  return response.getEntitiesList().map(toEntity);
}

// The response carries no total: a page shorter than PAGE_SIZE is the last one.
export async function listB2BCommercialTerms(): Promise<CommercialTermsRowDto[]> {
  const call = addAuthzMetadataGrpcCall(client, client.listCommercialTerms);
  const rows: CommercialTermsRowDto[] = [];
  for (let page = 0; ; page++) {
    const request = new ListCommercialTermsRequest();
    request.setPage(page);
    request.setSize(PAGE_SIZE);
    const pageRows = (await call(request)).getRowsList();
    rows.push(...pageRows.map(toRow));
    if (pageRows.length < PAGE_SIZE) {
      return rows;
    }
  }
}

export async function updateB2BCommercialTermsRow(
  row: CommercialTermsRowDto,
  change: CommercialTermsRowChangeDto
): Promise<CommercialTermsRowDto> {
  const request = new UpdateCommercialTermsRowRequest();
  request.setKey(toKey(row));
  request.setCommissionPct(change.commissionPct === undefined ? "" : String(change.commissionPct));
  request.setNet(change.net);
  request.setValidFrom(change.validFrom ?? "");
  request.setValidTo(change.validTo ?? "");
  request.setHotelCodesList(change.hotelCodes);
  const call = addAuthzMetadataGrpcCall(client, client.updateCommercialTermsRow);
  const response = await call(request);
  return toRow(response.getRow()!);
}

export async function deleteB2BCommercialTermsRow(row: CommercialTermsRowDto): Promise<void> {
  const request = new DeleteCommercialTermsRowRequest();
  request.setKey(toKey(row));
  const call = addAuthzMetadataGrpcCall(client, client.deleteCommercialTermsRow);
  await call(request);
}
